import React, { useMemo, useState } from 'react';
import PageHeader from '../../components/shared/PageHeader';
import { Card, StatusPill } from '../../components/shared/DataToolbar';
import { LoadingState, ErrorState, EmptyResults } from '../../components/shared/Pending';
import { useApi } from '../../api/useApi';
import { listTestimonials, updateTestimonial } from '../../api/adminApi';
import { Star, Check, X, Flag, Quote } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

export default function AdminTestimonials() {
  const { data, loading, error, refetch } = useApi(listTestimonials, []);
  const rows = useMemo(() => Array.isArray(data) ? data : (data?.items || data || []), [data]);
  const [tab, setTab] = useState('all');
  const filtered = tab === 'all' ? rows : rows.filter(t => t.status === tab);
  const count = (s) => rows.filter(t => s === 'all' ? true : t.status === s).length;
  const patch = async (t, payload) => { try { await updateTestimonial(t.id, payload); toast.success('Updated'); refetch(); } catch (e) { toast.error(e?.message); } };

  return (
    <div>
      <PageHeader title="Guest testimonials" description="Approve and manage homepage testimonials." />
      {loading ? <LoadingState /> : error ? <ErrorState error={error} onRetry={refetch} />
        : (
          <>
            <Card className="p-2 mb-4 inline-flex flex-wrap gap-1">
              {[['all','All'],['pending','Pending'],['published','Published'],['unpublished','Unpublished'],['rejected','Rejected']].map(([k, l]) => (
                <button key={k} onClick={() => setTab(k)} className={`px-3 py-1.5 rounded-md text-sm font-medium ${tab === k ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'}`}>{l} ({count(k)})</button>
              ))}
            </Card>
            {filtered.length === 0 ? <EmptyResults icon={Quote} title="No testimonials" />
              : (
                <div className="space-y-3">
                  {filtered.map(t => (
                    <Card key={t.id} className="p-5">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-slate-900">{t.title || 'Testimonial'}</div>
                          <div className="text-xs text-slate-500 mt-0.5">By {t.customer?.name} • {t.customer?.email}</div>
                          <div className="flex items-center gap-2 mt-2">
                            <div className="flex">{[1,2,3,4,5].map(i => <Star key={i} className={`h-3.5 w-3.5 ${i <= (t.rating||0) ? 'text-amber-400 fill-amber-400' : 'text-slate-200 fill-slate-200'}`} />)}</div>
                            <span className="text-xs text-slate-500">• {t.property?.title}</span>
                          </div>
                          <p className="mt-3 text-sm text-slate-700">“{t.body}”</p>
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          <StatusPill status={t.status} />
                          <div className="flex flex-col gap-1.5">
                            {t.status !== 'published' && <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 h-8 w-32 justify-start" onClick={() => patch(t, { status: 'published' })}><Check className="h-4 w-4 mr-1" /> Publish</Button>}
                            {t.status === 'published' && <Button size="sm" variant="outline" className="h-8 w-32 justify-start" onClick={() => patch(t, { status: 'unpublished' })}><X className="h-4 w-4 mr-1" /> Unpublish</Button>}
                            {t.status !== 'rejected' && <Button size="sm" variant="outline" className="h-8 w-32 justify-start text-rose-600 border-rose-200" onClick={() => patch(t, { status: 'rejected' })}><Flag className="h-4 w-4 mr-1" /> Reject</Button>}
                          </div>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )
            }
          </>
        )
      }
    </div>
  );
}
